import type { components } from "@verity/shared-types";
import { apiJson } from "./client";

export type DataSourceDTO = {
  id: string; // ds_...
  name: string;
  connection_type: string;

  // Type identifier and categorization
  ds_key: string; // e.g., "ga4", "finance"
  labels: string[]; // e.g., ["marketing", "analytics"]
  /**
   * Whether this source has a segmentation profile configured.
   *
   * THE segmentation-eligibility signal (plan 2026-05-10 Step G.1.c/d),
   * replacing the legacy `ds_key === "profile"` gate: a segmentable source is
   * just a normal BigQuery source with a config attached, so never filter on
   * `ds_key` for this. Mirrors `types/dataSources.ts`.
   */
  segmentation_configured: boolean;
  /**
   * Whether the customer asked for this source to be usable for segmentation.
   *
   * Requested but not yet `segmentation_configured` is the *pending* window:
   * the profile is only materialized after the source's first successful data
   * refresh. The segment builder shows these as not-yet-available options
   * instead of dropping them silently. Mirrors `types/dataSources.ts`.
   */
  connector_segmentation_requested: boolean;
  /** Public id of the BigQuery authorization backing this source (auth_…). */
  authorization_id?: string | null;
  /** Display name of that authorization, for context lines in forms. */
  authorization_name?: string | null;
  /** Connection config; for BigQuery holds `{ bigquery: { project_id, dataset_ids } }`
   *  — the scope the rest of the platform trusts for this source. */
  config?: Record<string, unknown>;
  ownership_mode: string; // customer, semantics, transform, managed
  is_managed: boolean; // computed: ownership_mode != "customer"
  sem_stage: string; // raw, mapped, curated, gold
  semantic_pack_key: string | null; // e.g., "ga4" if using a semantic pack

  status: "ACTIVE" | "DISABLED";

  // Customer-controlled opt-out for account-aggregate dashboards.
  // Default true on create — when false, this source's data is excluded
  // from Marketing Overview-type dashboards. Per-DS dashboard still works.
  include_in_account_overview: boolean;

  // Platform-provided source (weather, FX, website scan, unified profile,
  // the post-purchase survey). Verity provisioned it and the customer cannot
  // remove it. Mirrors `types/dataSources.ts`.
  is_platform_source?: boolean;
  /** Most recent fleet-seed outcome for this account; null = none recorded recently. */
  platform_seed_status?: "WRITTEN" | "NEEDS_HUMAN" | "TRANSIENT_ERROR" | null;
  platform_seed_last_at?: string | null;
  /** Answers held for a per-answer platform source (survey); 0 = none yet; null = no such count. */
  platform_answers?: number | null;
  /** Update fields the customer may change on this platform source (always "status"); null = ordinary source. */
  platform_controls?: string[] | null;
};

/** The refresh intervals a direct-connect source offers, in hours (the
 *  backend's `Literal[1, 4, 8, 24]` and DB CHECK). A type only: page tests
 *  mock this module wholesale. */
export type CacheRefreshHours = 1 | 4 | 8 | 24;

export type WorkspaceGrantSummaryDTO = {
  id: string; // ws_...
  name: string;
  type?: string | null;
};

export async function listDataSources(params?: {
  limit?: number;
  offset?: number;
  connection_type?: string;
  ds_key?: string;
  family?: string; // source family (e.g. "meta") — expands to every ds_key in it
  status?: string;
  workspace_id?: string;
  includes?: string; // e.g. "workspace_grants"
}): Promise<DataSourceDTO[]> {
  const q = new URLSearchParams();
  q.set("limit", String(params?.limit ?? 100));
  q.set("offset", String(params?.offset ?? 0));
  if (params?.ds_key) q.set("ds_key", params.ds_key);
  if (params?.family) q.set("family", params.family);
  if (params?.connection_type) q.set("connection_type", params.connection_type);
  if (params?.status) q.set("status", params.status);
  if (params?.workspace_id) q.set("workspace_id", params.workspace_id);
  if (params?.includes) q.set("includes", params.includes);

  // apiJson auto-unwraps { data: ... }, returns DataSourceDTO[] directly
  return apiJson<DataSourceDTO[]>(`/data-sources?${q.toString()}`);
}

export async function listDataSourceWorkspaceGrants(dataSourceId: string) {
  // Backend returns { data: { workspaces: [...] } }, apiJson unwraps to { workspaces: [...] }
  return apiJson<{ workspaces: WorkspaceGrantSummaryDTO[] }>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/grants/workspaces`
  );
}

export async function setDataSourceWorkspaceGrant(opts: {
  dataSourceId: string;
  workspaceId: string;
  enabled: boolean;
}) {
  const { dataSourceId, workspaceId, enabled } = opts;
  await apiJson<void>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/grants/workspaces/${encodeURIComponent(workspaceId)}`,
    { method: enabled ? "POST" : "DELETE" }
  );
}

export async function updateDataSource(
  dataSourceId: string,
  payload: {
    name?: string;
    status?: "ACTIVE" | "DISABLED";
    authorization_id?: string;
    // Type identifier and categorization
    ds_key?: string;
    labels?: string[];
    sem_stage?: string;
    // BigQuery config fields
    project_id?: string | null;
    dataset_ids?: string[] | null;
    table_ids?: string[] | null;
    // Account-aggregate opt-out — null/undefined = no change
    include_in_account_overview?: boolean;
    // Default region for a Verity-provided weather source (plan
    // 2026-08-19, Decision 3). Only mutable field besides `status` on a
    // platform source — the backend rejects anything else with a 4xx.
    default_region?: string;
    // Semantic layer of a direct-connect source (dbt plan 2026-10-05, step 6b)
    semantic_mode?: "verity" | "dbt" | "both";
    semantic_precedence?: "dbt" | "verity";
    // How often a direct-connect source's dashboard tiles may reload, in
    // hours (plan 2026-10-21 refresh interval, D1). A number, never a
    // string: the BFF allowlist and the backend refuse "4". Account
    // OWNER/ADMIN only; a managed source refuses it (422).
    cache_refresh_hours?: CacheRefreshHours;
  }
): Promise<DataSourceDTO> {
  // apiJson auto-unwraps { data: DataSourceDTO }
  return apiJson<DataSourceDTO>(
    `/data-sources/${encodeURIComponent(dataSourceId)}`,
    { method: "PATCH", body: JSON.stringify(payload) }
  );
}

/**
 * Deletes a source that nothing of the customer's hangs off. A source with
 * segments, dashboards or other work on it answers 409 `consent_required`:
 * delete those through `DeleteSourceDialog` (`getDeleteImpact` +
 * `deleteDataSourceWithConsent`). Kept for callers that roll back a source
 * they just created, which has nothing on it yet.
 */
export async function deleteDataSource(dataSourceId: string) {
  await apiJson<void>(`/data-sources/${encodeURIComponent(dataSourceId)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Delete with consent (plan 2026-09-22-deleting-a-source-shows-what-goes-with-it)
// ---------------------------------------------------------------------------

/** What deleting a source takes with it, what stays, and what stops it. */
export type DeleteImpact = components["schemas"]["DeleteImpact"];
/** One kind of object in the preview, e.g. `segments`, with up to 10 names. */
export type DeleteImpactKind = components["schemas"]["DeleteImpactKind"];
/** Something the delete cannot clean up itself; `code` is a machine token. */
export type DeleteImpactBlocker = components["schemas"]["DeleteImpactBlocker"];
/** The consent: the `kind` keys agreed to, and the preview's `impact_token`. */
export type SourceDeleteRequest = components["schemas"]["SourceDeleteRequest"];
/** `warnings`: clean-up outside Verity that is still being retried. */
export type SourceDeleteResult = components["schemas"]["SourceDeleteResult"];

/**
 * GET /data-sources/:id/delete-impact. Works for managed sources too: pass the
 * managed source's `data_source_id`.
 */
export async function getDeleteImpact(dataSourceId: string): Promise<DeleteImpact> {
  return apiJson<DeleteImpact>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/delete-impact`
  );
}

/**
 * POST /data-sources/:id/delete. A refusal is an `ApiError` with status 409
 * and `detail.code` `consent_required`, `impact_changed` or `blocked`; the
 * 409 carries counts only, never names, so re-read the preview for those.
 */
export async function deleteDataSourceWithConsent(
  dataSourceId: string,
  body: SourceDeleteRequest
): Promise<SourceDeleteResult> {
  return apiJson<SourceDeleteResult>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/delete`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

export type HealthCheckResult = {
  data_source_id: string;
  ok: boolean;
  error: string | null;
  checked_at: string | null;
};

export async function testDataSourceConnection(
  dataSourceId: string
): Promise<HealthCheckResult> {
  return apiJson<HealthCheckResult>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/health-check`,
    { method: "POST" }
  );
}

export async function createSqlDataSource(payload: {
  name: string;
  authorization_id: string;
  connection_type: "SQL";
  ds_key: string;
  labels?: string[];
  sem_stage?: string;
  workspace_id?: string;
  dialect: string;
  database?: string;
}): Promise<DataSourceDTO> {
  // apiJson auto-unwraps { data: DataSourceDTO }
  return apiJson<DataSourceDTO>(`/data-sources/sql`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Create a Snowflake DIRECT_CONNECT data source (ADR-0015 `customer` tier).
 *
 * Field names mirror `SnowflakeSourceCreateRequest`
 * (apps/backend/api/schemas/data_sources/snowflake_api.py) — note `schema`,
 * which is the alias the backend declares for `schema_name`.
 *
 * `private_key_pem` is the customer's own private key travelling inward. It is
 * never echoed back: the response is a plain DataSourceDTO, and the credential
 * lives encrypted on the Authorization row from here on.
 */
export async function createSnowflakeDataSource(payload: {
  name: string;
  ds_key: string;
  account: string;
  user: string;
  database: string;
  private_key_pem: string;
  warehouse?: string;
  role?: string;
  /** Alias for the backend's `schema_name` — limits discovery to one schema. */
  schema?: string;
  labels?: string[];
  sem_stage?: string;
  workspace_id?: string;
}): Promise<DataSourceDTO> {
  // apiJson auto-unwraps { data: DataSourceDTO }
  return apiJson<DataSourceDTO>(`/data-sources/snowflake`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function setWorkspaceGrantForDataSource(opts: {
  dataSourceId: string;
  workspaceId: string;
  enabled: boolean;
}) {
  const { dataSourceId, workspaceId, enabled } = opts;
  await apiJson<void>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/grants/workspaces/${encodeURIComponent(workspaceId)}`,
    { method: enabled ? "POST" : "DELETE" }
  );
}

export async function listWorkspaceGrantsForDataSource(id: string) {
  return apiJson<{ workspaces: WorkspaceGrantSummaryDTO[] }>(
    `/data-sources/${encodeURIComponent(id)}/grants/workspaces`
  );
}

export async function createBigQueryDataSource(payload: {
  name: string;
  authorization_id: string;
  connection_type: "BIGQUERY";
  ds_key: string;
  labels?: string[];
  sem_stage?: string;
  workspace_id?: string;
  project_id?: string;
  dataset_ids?: string[];
  table_ids?: string[];
  semantic_pack_key?: string;
  // User-supplied DWH slug (Step 13). Omit to let the backend derive one.
  // Immutable post-creation — surfaced in BQ table names like
  // `vrt_marts.{connector_prefix}_{slug}__{table}`.
  dwh_slug?: string;
}): Promise<DataSourceDTO> {
  // apiJson auto-unwraps { data: DataSourceDTO }
  return apiJson<DataSourceDTO>(`/data-sources/bigquery`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Discovery & Preview
// ---------------------------------------------------------------------------

export type DiscoveredColumnDTO = {
  name: string;
  data_type: string;
  native_type: string;
  nullable: boolean;
  description: string | null;
};

export type DiscoveredTableDTO = {
  name: string;
  full_name: string;
  schema_name: string | null;
  dataset_id: string | null;
  columns: DiscoveredColumnDTO[];
  row_count: number | null;
  size_bytes: number | null;
};

export type DiscoveryResultDTO = {
  tables: DiscoveredTableDTO[];
  discovered_at: string;
  schema_hash: string;
  table_count: number;
  column_count: number;
  warnings: string[];
};

export type CachedDiscoveryResponse = {
  data: DiscoveryResultDTO | null;
  cached: boolean;
};

export async function getCachedDiscovery(
  dataSourceId: string
): Promise<CachedDiscoveryResponse> {
  // This endpoint wraps in { data: { data: ..., cached: ... } }
  // apiJson unwraps the outer { data: ... }, giving us { data: DiscoveryResultDTO | null, cached: bool }
  return apiJson<CachedDiscoveryResponse>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/discovery/cached`
  );
}

export type PreviewColumn = {
  name: string;
  type: string;
};

export type PreviewResult = {
  columns: PreviewColumn[];
  rows: Record<string, unknown>[];
  row_count: number;
  truncated: boolean;
};

export async function previewDataSourceTable(
  dataSourceId: string,
  tableName: string,
  limit: number = 100
): Promise<PreviewResult> {
  const q = new URLSearchParams({
    table: tableName,
    limit: String(limit),
  });
  // apiJson auto-unwraps { data: PreviewResult }
  return apiJson<PreviewResult>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/preview?${q.toString()}`
  );
}

/**
 * Trigger an on-demand visitor_segments rebuild for a data source.
 *
 * Account ADMIN/OWNER only (backend enforces; the UI gates as
 * defense-in-depth). Rate-limited 10/hour per data_source. 202 = queued,
 * 409 = already in flight, 429 = rate-limited.
 *
 * Plan: docs/plans-pending/2026-05-11-symmetric-on-demand-publish-and-rebuild-ui.md
 */
export async function rebuildVisitorSegments(
  dataSourcePublicId: string
): Promise<{ request_id: string; status: string }> {
  return apiJson(
    `/data-sources/${encodeURIComponent(dataSourcePublicId)}/visitor-segments/rebuild`,
    { method: "POST", unwrapData: false }
  );
}


// ============================================================
// Profile-table schema drift
// Plan: docs/plans-pending/2026-07-27-profile-schema-drift-detection-and-remap.md
// ============================================================

/** A column whose data type changed between two observed schemas. */
export type RetypedColumn = {
  column: string;
  /** Previous BigQuery type, e.g. "STRING". */
  from: string;
  /** Current BigQuery type, e.g. "ARRAY<STRING>". */
  to: string;
};

/**
 * One detected schema change on a table this source reads.
 *
 * Mirrors the backend's `SchemaDriftEventDTO`. The event's `impact` blob is
 * deliberately NOT part of this contract — it holds workspace-scoped segment
 * names and this endpoint is account-scoped. Column names and table FQNs are
 * the customer's own schema and are safe here.
 */
export type SchemaDriftEvent = {
  id: string;
  /** `column_diff` — columns changed. `table_missing` — the table is gone. */
  kind: "column_diff" | "table_missing";
  table_fqn: string;
  removed_columns: string[];
  added_columns: string[];
  retyped_columns: RetypedColumn[];
  /** `breaking` — something references the changed column. */
  severity: "breaking" | "informational";
  status: "open" | "resolved" | "dismissed";
  detected_at: string;
  resolved_at: string | null;
};

export type SchemaDriftResponse = {
  /** Needs customer action. Effectively breaking-only: informational events
   *  auto-resolve in the capture that creates them. */
  open: SchemaDriftEvent[];
  /** The most recent closed events, newest first. */
  history: SchemaDriftEvent[];
};

/**
 * Schema changes detected on a data source's profile tables.
 *
 * Readable by any account member — a member has to be able to see why their
 * segments stopped refreshing even though only an admin can run a check or fix
 * it. 404 when the source does not exist, belongs to another account, or has no
 * profile configuration.
 */
export async function getSchemaDrift(
  dataSourcePublicId: string
): Promise<SchemaDriftResponse> {
  return apiJson<SchemaDriftResponse>(
    `/data-sources/${encodeURIComponent(dataSourcePublicId)}/schema-drift`,
    { unwrapData: false }
  );
}

export type SchemaCheckTableResult = {
  table_fqn: string;
  /** `ok` | `changed` | `missing` | `unreadable` — see the backend route. */
  status: string;
};

export type SchemaCheckResponse = {
  tables: SchemaCheckTableResult[];
  drift_events_created: number;
  breaking_events_created: number;
  events_resolved: number;
};

/**
 * Run an on-demand schema check against the source's live tables.
 *
 * Account ADMIN/OWNER only and rate-limited 6/hour per account (each call
 * spends a live BigQuery metadata query per configured table), so callers must
 * handle 429. 503 means the read failed — the message is already customer-safe.
 */
export async function runSchemaCheck(
  dataSourcePublicId: string
): Promise<SchemaCheckResponse> {
  return apiJson<SchemaCheckResponse>(
    `/data-sources/${encodeURIComponent(dataSourcePublicId)}/schema-check`,
    { method: "POST", unwrapData: false }
  );
}

// ------------------------------------------------------------------
// Remap wizard reads (plan step 3.1)
//
// Both routes are ADMIN/OWNER and both are READ-ONLY: the wizard applies
// what they propose through the existing segment / event-rule /
// profile-config / destination endpoints, each of which keeps its own gate
// and validation. Nothing here writes.
// ------------------------------------------------------------------

/** One affected object, collapsed from its individual column references. */
export type SchemaDriftImpactObject = {
  /** `segment` | `event_rule` | `data_source_profile` |
   *  `data_source_profile_identifier` | `brevo_destination` | … */
  object_type: string;
  /** Customer-facing public id (`seg_…`), or null for internally-keyed rows
   *  such as the profile configuration itself. */
  object_id: string | null;
  object_label: string;
  /** False for semantic-layer field mappings: flagged, never rewritten here. */
  remappable: boolean;
  columns: string[];
  locations: string[];
};

/** A segment affected *through* another object rather than directly. */
export type SchemaDriftIndirection = {
  object_type: string;
  object_id: string | null;
  object_label: string;
  location: string;
  target_kind: string;
  target_public_id: string;
};

/** One candidate replacement column for a removed column. */
export type SchemaDriftSuggestion = {
  column: string;
  /** 0..1 — higher is a better guess. */
  score: number;
  data_type_matches: boolean;
  added_in_this_change: boolean;
};

export type SchemaDriftDetail = {
  event: SchemaDriftEvent;
  /** The key the rule tree uses for this table: `""` for the profile table,
   *  the configured name for a related table. `null` means the source no
   *  longer reads this table, so nothing can be remapped on it. Test for
   *  `null` explicitly — `""` is a valid value. */
  table_key: string | null;
  impact: SchemaDriftImpactObject[];
  indirections: SchemaDriftIndirection[];
  /** {removed column: ranked candidates}. Empty for `table_missing`. */
  suggestions: Record<string, SchemaDriftSuggestion[]>;
  /** Retyped columns get no suggestions — the column kept its name, so the
   *  fix is the operator, not a remap. */
  retyped: RetypedColumn[];
};

/**
 * One drift event in full, with freshly recomputed impact.
 *
 * Account ADMIN/OWNER only — this is where segment names live. 404 when the
 * event does not exist, belongs to another account, or hangs off a different
 * source.
 */
export async function getSchemaDriftDetail(
  dataSourcePublicId: string,
  eventId: string
): Promise<SchemaDriftDetail> {
  return apiJson<SchemaDriftDetail>(
    `/data-sources/${encodeURIComponent(dataSourcePublicId)}/schema-drift/${encodeURIComponent(eventId)}`,
    { unwrapData: false }
  );
}

/** One customer decision: map this column somewhere, or drop its references. */
export type SchemaRemapAction = {
  /** `""` is the profile table — a valid value, not a missing one. */
  table_key: string;
  column: string;
  action: "rename" | "remove";
  /** Required for `rename`, must be absent for `remove`. */
  new_column?: string;
};

/** One rule the rewrite would delete, and what that does to the audience. */
export type SchemaRemapRemoval = {
  column: string;
  table_key: string;
  location: string;
  /** `widens` — a condition dropped from an AND. `narrows` — a branch dropped
   *  from an OR, which silently shrinks the audience. */
  effect: string;
};

/** A field the requested action cannot be applied to. */
export type SchemaRemapRefusal = {
  field: string;
  column: string;
  /** `required_field` — the field is load-bearing and cannot be cleared. */
  refusal: string;
};

export type SchemaRemapObject = {
  object_type: string;
  public_id: string | null;
  name: string;
  /** Only the fields that CHANGED, named as the applying endpoint's payload
   *  fields, so they forward without translation. `null` = nothing to apply. */
  proposed: Record<string, unknown> | null;
  removals: SchemaRemapRemoval[];
  emptied_event_blocks: number;
  /** The rewrite would leave a segment with no effective rules — a
   *  match-everyone audience. Never applied; pause or edit instead. */
  rejected_would_empty: boolean;
  refusals: SchemaRemapRefusal[];
  unchanged: boolean;
};

export type SchemaRemapPlan = {
  objects: SchemaRemapObject[];
  total_objects: number;
  rejected_segments: string[];
  narrowing_removals: number;
  widening_removals: number;
};

/**
 * What the chosen remap actions would do — computed, never applied.
 *
 * Account ADMIN/OWNER, rate-limited 10/min. 409 (with a customer-safe
 * message) when the event has already been resolved elsewhere.
 */
export async function planSchemaRemap(
  dataSourcePublicId: string,
  eventId: string,
  actions: SchemaRemapAction[]
): Promise<SchemaRemapPlan> {
  return apiJson<SchemaRemapPlan>(
    `/data-sources/${encodeURIComponent(dataSourcePublicId)}/schema-drift/${encodeURIComponent(eventId)}/plan-remap`,
    {
      method: "POST",
      body: JSON.stringify({ actions }),
      headers: { "Content-Type": "application/json" },
      unwrapData: false,
    }
  );
}


// ============================================================
// DWH slug — Step 13 of per-DataSource DWH-isolation plan
// ============================================================

export type CheckSlugReason = "taken" | "invalid" | "reserved";

export type CheckSlugResponse = {
  available: boolean;
  reason: CheckSlugReason | null;
};

/**
 * Check whether a DWH slug is available in the caller's account.
 *
 * The endpoint is account-scoped via JWT and never leaks existence across
 * tenants — a slug taken in another account is reported as `{available: true}`.
 *
 * Advisory only: the authoritative check happens at INSERT time via the
 * partial unique index `uq_data_sources_account_dwh_slug`. UI callers must
 * still handle a race-loss 409 on save.
 */
export async function checkDwhSlug(slug: string): Promise<CheckSlugResponse> {
  const q = new URLSearchParams({ slug });
  return apiJson<CheckSlugResponse>(
    `/data-sources/check-slug?${q.toString()}`,
    { unwrapData: false },
  );
}
